import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
} from '@chakra-ui/react';
import { useSnackbar } from '@components/snackbar';
import { LeftArrowIcon, SketchNoteLogoIcon } from '@sketchnote/icons';
import { Avatar } from '@sketchnote/shared-components';
import classNames from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';
import { API } from 'src/api';
import Analytics from 'src/api/services/analytics/analytics';
import { useAuth } from 'src/shared/hooks/use-auth';

import { SidebarWrapper } from './components/wrapper';
const { useState, useEffect, useMemo } = React;

const analyticsHandler = (category) => {
  Analytics.track('Template store category select', {
    'Template store Category': category,
  });
};

export interface TemplateStoreProps {
  isOpen: boolean;
  onToggleSidebar?: () => void;
}

export const TemplateStoreSideBar = ({
  isOpen,
  onToggleSidebar,
}: TemplateStoreProps): JSX.Element => {
  const router = useRouter();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;
  const authData = useAuth((s) => s.userDetails);

  const currentOrganisation = useMemo(
    () =>
      orgId ? authData?.organisations?.filter((item) => item.organisationId === orgId)[0] : '',
    [router.query.orgId, authData?.organisations]
  );

  return (
    <SidebarWrapper isOverview isOpen={isOpen} onToggle={onToggleSidebar} showHome={false}>
      <div className="overflow-scroll">
        {authData?.authentication === 'authorised' && (
          <Link href={`/w/${orgId}/dashboard`}>
            <a>
              <button className="my-8 text-blue-500 px-24">
                <LeftArrowIcon className="mr-13 text-sm mt-2" />
                <span className="text-sm font-medium">Back to home</span>
              </button>
            </a>
          </Link>
        )}
        <Accordion className="px-16" defaultIndex={[0]} allowMultiple>
          <AccordionItem>
            <h3>
              <AccordionButton className="flex justify-between" _focus={{ boxShadow: 'none' }}>
                <div className="flex items-center">
                  <div className=" bg-primary-500 rounded-full w-24 h-24 px-5 mr-8 flex items-center justify-items-center">
                    <SketchNoteLogoIcon fill="white" size={14} />
                  </div>
                  <span className=" font-medium text-sm text-gray-600">Community</span>
                </div>
                <AccordionIcon />
              </AccordionButton>
            </h3>
            <AccordionPanel>
              <TemplateCategories />
            </AccordionPanel>
          </AccordionItem>
          {currentOrganisation?.organisationId && (
            <AccordionItem>
              <h3>
                <AccordionButton className="flex justify-between" _focus={{ boxShadow: 'none' }}>
                  <div className="flex items-center">
                    <div className="mr-8">
                      <Avatar
                        wrapperClassName="mt-2"
                        name={currentOrganisation?.name}
                        size="sm"
                        src={currentOrganisation?.avatar}
                        type="photo"
                        tooltip={false}
                      />
                    </div>
                    <span className=" font-medium text-sm text-gray-600">
                      {currentOrganisation?.name}
                    </span>
                  </div>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel>
                <TemplateCategories org={orgId} />
              </AccordionPanel>
            </AccordionItem>
          )}
        </Accordion>
      </div>
    </SidebarWrapper>
  );
};

export const TemplateCategories = ({ org }: { org?: string | string[] | undefined }) => {
  const authData = useAuth((s) => s.userDetails);
  const router = useRouter();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;

  const currentOrganisation = useMemo(
    () =>
      orgId ? authData?.organisations?.filter((item) => item.organisationId === orgId)[0] : '',
    [router.query.orgId, authData?.organisations]
  );

  const [categories, setCategories] = useState<any>([]);
  const [openSnackbar] = useSnackbar();

  const fetchCategories = async () => {
    try {
      const catergoryParams = org && typeof org === 'string' ? { org } : {};
      const response = await API.get(`/files/category`, { params: catergoryParams });
      setCategories(response.data.data);
    } catch (error) {
      openSnackbar({
        text: `Failed to load categories for ${currentOrganisation?.name || 'organisation'}`,
        snackType: 'error',
        position: 'bottom-center',
      });
    }
  };

  const isActive = (item: string) => {
    if (org && typeof org === 'string' && router.pathname.includes('/org')) {
      return router.query?.slug === item;
    } else if (org === undefined && !router.pathname.includes('/org')) {
      return router.query?.slug === item;
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [org]);
  return (
    <div>
      <div className="px-24">
        <p className="font-medium text-xs text-gray-500 mb-8">Categories</p>
        {categories.length > 0 &&
          categories.map((category) => (
            <Link
              href={
                authData?.authentication === 'authorised'
                  ? `/${mode}/${orgId}/template-store/${org ? 'org/' : ''}category/${category.slug}`
                  : `/template-store/category/${category.slug}`
              }
              key={category.slug}
            >
              <a
                className={classNames({
                  'font-medium text-sm text-gray-600': true,
                  'text-primary-500 bg-primary-100 hover:bg-blue-200 rounded': isActive(
                    category.slug
                  ),
                })}
              >
                <div
                  className="py-10 min-w-40 flex flex-row"
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    analyticsHandler(category.title);
                  }}
                  onKeyPress={() => {
                    analyticsHandler(category.title);
                  }}
                >
                  <span>
                    {category?.title?.length > 25
                      ? `${category?.title.substring(0, 25)}...`
                      : category.title}
                  </span>
                </div>
              </a>
            </Link>
          ))}
        {categories.length < 1 && (
          <span className=" text-gray-600 text-sm">No categories created</span>
        )}
      </div>
    </div>
  );
};
